import React, { useCallback, useMemo } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { AssociatedCrmObjectInput } from '@/lib/crm/components/associations/AssociatedCrmObjectInput';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { FilterCondition, OperatorType } from '@/lib/crm/filters/types';
import type { FieldTypeSchema } from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject, FilterValue } from '@/types/crm';

import { PropertyValueInput } from './PropertyValueInput';
import {
  ASSOCIATION_FILTER_OPERATORS,
  ASSOCIATION_OPERATOR_LABELS,
  type AssociationOperatorType,
  getAvailableOperators,
  needsValueInput,
  OPERATOR_LABELS,
  selectionModeForAssociationOperator,
} from './shared';

interface AssociationConditionBodyProps {
  condition: FilterCondition;
  associationType: AssociationType | undefined;
  teamId: number;
  /** Pre-fetched target property map (property_type_name → schema). */
  targetPropertyTypeMap: Record<string, PropertyTypeSchema>;
  /** Shared org-global field type map (for operator lookup). */
  fieldTypeMap: Record<string, FieldTypeSchema>;
  operatorSelectRef: React.RefObject<HTMLDivElement | null>;
  onUpdate: (id: string, updates: Partial<FilterCondition>) => void;
  onKeyDown: React.KeyboardEventHandler;
  onAssociationObjectsChange?: (objects: Record<string, CrmObject>) => void;
}

/**
 * Controls below the field combobox for an ASSOCIATION condition: an operator
 * select and the matching value input.
 *
 * When `condition.target_property` is set (field mode), shows property-driven
 * operators + property value input. Otherwise shows association operators
 * (Is / Is Not / …) + record picker.
 *
 * The old "Filter by" dropdown has been removed — association properties are
 * now directly selectable in the parent combobox, so users pick the target
 * property (or "Specific {type}") in one step.
 */
export function AssociationConditionBody({
  condition,
  associationType,
  teamId,
  targetPropertyTypeMap,
  fieldTypeMap,
  operatorSelectRef,
  onUpdate,
  onKeyDown,
  onAssociationObjectsChange,
}: AssociationConditionBodyProps) {
  const targetProperty = condition.target_property
    ? targetPropertyTypeMap[condition.target_property]
    : undefined;
  const isFieldMode = !!condition.target_property;

  const availableOperators = useMemo(() => {
    if (isFieldMode) {
      return targetProperty
        ? getAvailableOperators(targetProperty, fieldTypeMap)
        : [];
    }
    return ASSOCIATION_FILTER_OPERATORS;
  }, [isFieldMode, targetProperty, fieldTypeMap]);

  const operatorItems = useMemo(
    () =>
      availableOperators.map(op => ({
        id: op,
        label: isFieldMode
          ? OPERATOR_LABELS[op as OperatorType]
          : ASSOCIATION_OPERATOR_LABELS[op as AssociationOperatorType],
      })),
    [availableOperators, isFieldMode]
  );

  const handleOperatorChange = useCallback(
    (key: React.Key | null) => {
      if (key === null) {
        return;
      }
      const newOperator = key as OperatorType;
      let newValue: FilterValue;
      if (!needsValueInput(newOperator)) {
        newValue = null;
      } else if (isFieldMode) {
        // Field mode uses property-value semantics; keep the current value.
        newValue = condition.value;
      } else if (
        (newOperator === 'equals' || newOperator === 'not_equals') &&
        Array.isArray(condition.value)
      ) {
        // Collapse array (from in/not_in) to a scalar for single-value ops.
        newValue = condition.value[0] ?? null;
      } else if (
        (newOperator === 'in' || newOperator === 'not_in') &&
        !Array.isArray(condition.value) &&
        condition.value !== null &&
        condition.value !== undefined
      ) {
        // Wrap scalar (from equals/not_equals) into an array for multi ops.
        newValue = [String(condition.value)];
      } else {
        newValue = condition.value;
      }
      onUpdate(condition.id, { operator: newOperator, value: newValue });
    },
    [condition.id, condition.value, isFieldMode, onUpdate]
  );

  const handleAssocValueChange = useCallback(
    (newValue: string[] | null) => {
      const isSingleValue =
        condition.operator === 'equals' || condition.operator === 'not_equals';
      if (isSingleValue) {
        const singleValue =
          newValue && newValue.length > 0 ? newValue[0] : null;
        onUpdate(condition.id, { value: singleValue });
      } else {
        onUpdate(condition.id, { value: newValue || [] });
      }
    },
    [condition.id, condition.operator, onUpdate]
  );

  const handlePropertyValueChange = useCallback(
    (newValue: FilterValue) => onUpdate(condition.id, { value: newValue }),
    [condition.id, onUpdate]
  );

  let valueInput: React.ReactNode = null;
  if (needsValueInput(condition.operator)) {
    if (isFieldMode) {
      if (targetProperty) {
        valueInput = (
          <PropertyValueInput
            property={targetProperty}
            operator={condition.operator}
            value={condition.value}
            onChange={handlePropertyValueChange}
          />
        );
      }
    } else if (associationType) {
      let arrValue: string[];
      if (Array.isArray(condition.value)) {
        arrValue = condition.value.map(v => String(v));
      } else if (condition.value) {
        arrValue = [String(condition.value)];
      } else {
        arrValue = [];
      }
      valueInput = (
        <AssociatedCrmObjectInput
          associationType={associationType}
          value={arrValue}
          onChange={handleAssocValueChange}
          orgId={teamId}
          disabled={false}
          hideCreateOption
          selectionMode={selectionModeForAssociationOperator(
            condition.operator as AssociationOperatorType
          )}
          onSelectedObjectsChange={onAssociationObjectsChange}
        />
      );
    }
  }

  return (
    // Operator + value share one line where the width allows (wrapping on
    // narrow rails) — mirrors FilterConditionRow's property path so both
    // condition shapes have the same density. The operator sizes to its
    // label so it never truncates; the value takes the remaining width.
    <div className='flex flex-wrap items-start gap-1.5'>
      <div className='flex-none'>
        <Select
          ref={operatorSelectRef}
          aria-label='Filter operator'
          selectedKey={condition.operator}
          onSelectionChange={handleOperatorChange}
          items={operatorItems}
        >
          {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>
      {valueInput !== null && (
        <div className='min-w-40 flex-1 basis-40' onKeyDown={onKeyDown}>
          {valueInput}
        </div>
      )}
    </div>
  );
}
